import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import axiosInstance from "../../../services/server/axios";
import { useCurrency } from "../../../context/CurrencyContext";

const PayopPayment = ({ offer, discount, couponCode, couponId, onSuccess }) => {
  const { t } = useTranslation();
  const { formatPrice, currency, getSymbol } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [errorDetails, setErrorDetails] = useState(null);

  const handlePayopPayment = async () => {
    try {
      setLoading(true);
      setErrorDetails(null);

      const finalAmount = formatPrice(offer.montant - discount);

      console.log("Sending PayOP payment request:", {
        offer_id: offer.id,
        amount: finalAmount,
        currency: currency,
        discount: discount,
        finalAmount: finalAmount,
      });

const response = await axiosInstance.post("/payop/create-invoice", {
        offer_id: offer.id,
        amount: finalAmount,
        currency: currency,
        discount: discount,
        coupon_id: couponId,
        language: "en",  
      });

      

      console.log("PayOP payment response:", response.data);

      if (response.data.success && response.data.data?.checkout_url) {
        const checkoutUrl = response.data.data.checkout_url;
        const invoiceId = response.data.data.invoice_id || response.data.data.id;

        setPaymentUrl(checkoutUrl);
        setInvoiceId(invoiceId);

        // Store payment info in localStorage for callback handling
        const paymentInfo = {
          invoiceId: invoiceId,
          offerId: offer.id,
          amount: finalAmount,
          currency: currency,
          couponCode: couponCode,
          discount: discount,
          timestamp: Date.now(),
          method: 'payop',
          // Store achat_id if available from initial response
          achatId: response.data.data?.achat_id || null
        };
        localStorage.setItem('pendingPayopPayment', JSON.stringify(paymentInfo));

        // Redirect to PayOP payment page in same tab
        window.location.href = checkoutUrl;

      } else {
        const errorMessage = response.data.message || t("payop.failed_to_create");
        setErrorDetails({
          message: errorMessage,
          fullResponse: response.data
        });
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("PayOP payment error:", error);
      console.error("Error response:", error.response?.data);

      const errorData = error.response?.data;
      let errorMessage = '';

      if (errorData) {
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string'
            ? errorData.error
            : JSON.stringify(errorData.error);
        } else {
          errorMessage = JSON.stringify(errorData);
        }
      } else {
        errorMessage = error.message || t("payop.general_error");
      }

      // Check for signature error
      const errorMessageStr = String(errorMessage).toLowerCase();
      if (errorMessageStr.includes('wrong signature') || errorMessageStr.includes('signature')) {
        toast.error(t("payop.signature_error"));
      } else {
        toast.error(errorMessage || t("payop.general_error"));
      }

      setErrorDetails({
        message: errorMessage,
        code: error.response?.status,
        fullError: errorData,
      });
    } finally {
      setLoading(false);
    }
  };

  const checkPendingPayment = async () => {
    try {
      const pendingPaymentStr = localStorage.getItem('pendingPayopPayment');
      if (!pendingPaymentStr) return;

      const pendingPayment = JSON.parse(pendingPaymentStr);
      const now = Date.now();
      const oneHour = 60 * 60 * 1000;

      // Check if payment info is older than 1 hour
      if (now - pendingPayment.timestamp > oneHour) {
        localStorage.removeItem('pendingPayopPayment');
        return;
      }

      // Check payment status using invoice ID
      if (pendingPayment.invoiceId) {
        const response = await axiosInstance.get(`/payop/status/${pendingPayment.invoiceId}`);
        if (response.data.success && response.data.data?.status === 'completed') {
          // Update pending payment with achat_id if available
          if (response.data.data?.achat_id) {
            const updatedPayment = {
              ...pendingPayment,
              achatId: response.data.data.achat_id
            };
            localStorage.setItem('pendingPayopPayment', JSON.stringify(updatedPayment));
          }

          // Payment completed, trigger success
          onSuccess({
            paymentID: pendingPayment.invoiceId,
            method: 'payop',
            amount: pendingPayment.amount,
            currency: pendingPayment.currency,
            status: 'completed',
            achat: { id: response.data.data.achat_id },
          });
          localStorage.removeItem('pendingPayopPayment');
        }
      }
    } catch (error) {
      console.error('Error checking pending PayOP payment:', error);
    }
  };

  // Check for pending payments when component mounts
  useEffect(() => {
    checkPendingPayment();

    // Also check when window gains focus (user returns from payment tab)
    const handleFocus = () => {
      checkPendingPayment();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  if (paymentUrl) {
    return (
      <div className="text-center p-3">
        <div className="alert alert-info">
          <h6>{t("payop.payment_title")}</h6>
          <p>{t("payop.payment_page_opened")}</p>
          <p>
            {t("payop.page_did_not_open")}{" "}
            <a
              href={paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-primary"
            >
              {t("payop.click_to_pay")}
            </a>
          </p>
        </div>
        <div className="alert alert-warning small">
          <strong>{t("payop.note")}:</strong> {t("payop.redirect_note")}
        </div>
        <button
          className="btn btn-secondary mt-2"
          onClick={() => {
            setPaymentUrl('');
            setLoading(false);
          }}
          disabled={loading}
        >
          {t("payop.try_different_method")}
        </button>
      </div>
    );
  }

  const finalAmount = formatPrice(offer.montant - discount);

  return (
    <div className="payop-payment-container">
      <div className="payment-info mb-3 p-3  rounded">
        <div className="d-flex justify-content-between mb-2">
          <span className="">{t("payop.amount")}:</span>
          <strong className="text-primary">{getSymbol()}{finalAmount}</strong>
        </div>
        <div className="d-flex justify-content-between">
          <span className="">{t("payop.coins")}:</span>
          <strong>{offer.coins}</strong>
        </div>
      </div>

      <button
        onClick={handlePayopPayment}
        disabled={loading}
        className="btn btn-primary w-100 py-3"
        style={{
          // backgroundColor: "#6f42c1",
          // borderColor: "#6f42c1",
          fontSize: "16px",
          fontWeight: "600",
          opacity: loading ? 0.7 : 1
        }}
      >
        {loading ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            {t("payop.processing")}
          </>
        ) : (
          <>
            <i className="fas fa-lock me-2"></i>
            {t("payop.pay_with_payop")} - {getSymbol()}{finalAmount}
          </>
        )}
      </button>

      <div className="mt-3 text-center">
        <small className="d-block">
          <i className="fas fa-shield-alt me-1"></i>
          {t("payop.secure_payment")}
        </small>
        <small className="">
          {t("payop.payment_methods")}
        </small>
      </div>

      {/* Payment Logos Section */}

      <div className="d-flex mt-4 pt-4 justify-content-center align-items-center gap-4">
        <div className="bg-white p-2 rounded shadow-sm">
          <img
            src="/images/payments/Revolut-Logo.png"
            alt="Revolut"
            className="img-fluid"
            style={{ height: "45px", width: "auto" }}
          />
        </div>
        <div className="bg-white p-2 rounded shadow-sm">
          <img
            src="/images/payments/Monzo-Logo.png"
            alt="Monzo"
            className="img-fluid"
            style={{ height: "45px", width: "auto" }}
          />
        </div>
      </div>


      {/* Error Debug Section */}
      {/* {errorDetails && (
        <div className="mt-3">
          <div className="alert alert-danger">
            <h6>{t("payop.error_details")}</h6>
            <p><strong>{t("payop.message")}:</strong> {String(errorDetails.message)}</p>
            {errorDetails.code && <p><strong>{t("payop.code")}:</strong> {errorDetails.code}</p>}

            <details className="mt-2">
              <summary className="btn btn-sm btn-outline-danger">{t("payop.show_debug_details")}</summary>
              <div className="mt-2 p-2 bg-dark text-light rounded small">
                {errorDetails.fullError && (
                  <>
                    <p><strong>{t("payop.backend_response")}:</strong></p>
                    <pre>{JSON.stringify(errorDetails.fullError, null, 2)}</pre>
                  </>
                )}

                <p className="mt-2"><strong>{t("payop.backend_fixes_needed")}:</strong></p>
                <ol className="mb-0">
                  <li>{t("payop.fix_public_key")}</li>
                  <li>{t("payop.fix_secret_key")}</li>
                  <li>{t("payop.fix_merchant_id")}</li>
                  <li>{t("payop.fix_signature")}</li>
                  <li>{t("payop.fix_callback_urls")}</li>
                </ol>
              </div>
            </details>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default PayopPayment;